import React from 'react';
import { Card, CardContent } from '../components/shadcn/card';
import { Separator } from '../components/shadcn/separator';
import LegalPageLayout from '../components/LegalPageLayout';

const Refund: React.FC = () => {
  return (
    <LegalPageLayout
      badge="Legal"
      title="Refund & Returns Policy"
      subtitle="Your rights and our commitments regarding subscription refunds"
      effectiveDate="1 January 2026"
      lastUpdated="March 2026"
      currentPage="refund"
    >
      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-text mb-4 tracking-tight">1. Overview</h2>
        <p>
          This Refund and Returns Policy ("Policy") applies to all subscriptions and paid plans for the
          Paxley Code Security Platform, a product of Nucleus Systems (Pty) Ltd. This Policy is intended
          to be fair, transparent, and in compliance with the Consumer Protection Act 68 of 2008 (CPA)
          of South Africa and other applicable consumer protection legislation.
        </p>
        <p className="mt-4">
          We want you to be satisfied with your experience using Paxley Code Security Platform. If you
          are not fully satisfied, please read this Policy carefully to understand your rights and the
          process for requesting a refund.
        </p>
      </section>

      <Separator />

      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-text mb-4 tracking-tight">2. Subscription Plans</h2>
        <p>Paxley offers the following subscription types:</p>
        <ul className="list-disc list-inside space-y-2 mt-4">
          <li><strong className="text-text">Monthly Subscriptions:</strong> Billed once per month in advance.</li>
          <li><strong className="text-text">Annual Subscriptions:</strong> Billed once per year in advance at a discounted rate.</li>
          <li><strong className="text-text">Trial Plans:</strong> Free trial periods as offered from time to time.</li>
          <li><strong className="text-text">Custom / Enterprise Plans:</strong> Bespoke arrangements governed by a separate agreement.</li>
        </ul>
        <p className="mt-4">
          All payments are processed securely by <strong className="text-text">PAYSTACK</strong>{' '}
          (PCI-DSS compliant). Nucleus Systems does not store payment card details.
        </p>
      </section>

      <Separator />

      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-text mb-4 tracking-tight">3. Refund Eligibility</h2>

        <h3 className="font-display text-lg md:text-xl font-semibold text-text mt-6 mb-3 tracking-tight">3.1 Standard Refund Window</h3>
        <p>Refunds are available under the following conditions:</p>
        <ul className="list-disc list-inside space-y-2 mt-4">
          <li>The refund request is submitted within <strong className="text-text">14 (fourteen) calendar days</strong> of the initial subscription payment or renewal.</li>
          <li>The Service has not been meaningfully used — i.e., no code scans have been completed and no scan results have been accessed or downloaded during the subscription period.</li>
          <li>The Service was materially defective or substantially different from what was described at the time of purchase.</li>
        </ul>

        <h3 className="font-display text-lg md:text-xl font-semibold text-text mt-6 mb-3 tracking-tight">3.2 Annual Subscriptions</h3>
        <p>
          For annual subscriptions cancelled after the 14-day refund window, Nucleus Systems may, at its
          sole discretion, offer a pro-rated refund for unused full months remaining in the subscription
          period, minus any applicable processing fees. Such refunds are not guaranteed and are assessed
          on a case-by-case basis.
        </p>

        <h3 className="font-display text-lg md:text-xl font-semibold text-text mt-6 mb-3 tracking-tight">3.3 Free Trials</h3>
        <p>
          No refunds are applicable to free trial periods, as no payment is collected during the trial.
          At the end of a free trial, your subscription will only be charged if you explicitly choose to
          upgrade to a paid plan.
        </p>
      </section>

      <Separator />

      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-text mb-4 tracking-tight">4. Non-Refundable Circumstances</h2>
        <p>The following are not eligible for a refund:</p>
        <ul className="list-disc list-inside space-y-2 mt-4">
          <li>Subscriptions where one or more code scans have been completed and results have been delivered, regardless of whether the results were satisfactory.</li>
          <li>Subscription fees for periods already elapsed or consumed.</li>
          <li>Custom or enterprise plans governed by a separate contractual arrangement.</li>
          <li>Fees waived under promotional pricing, unless the Service was materially defective.</li>
          <li>Requests submitted more than 14 calendar days after the relevant payment date (subject to Section 5 below).</li>
          <li>Cancellations made because the user changed their mind after meaningful use of the Service.</li>
        </ul>
      </section>

      <Separator />

      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-text mb-4 tracking-tight">5. Consumer Protection Act (CPA) — South African Users</h2>
        <p>
          Nothing in this Policy limits or excludes the rights of consumers under the Consumer
          Protection Act 68 of 2008 (CPA) of South Africa. Under the CPA, you have the right to:
        </p>
        <ul className="list-disc list-inside space-y-2 mt-4">
          <li>Cancel a fixed-term agreement with 20 business days' written notice, subject to a reasonable cancellation penalty.</li>
          <li>Return goods (or cancel services) within 5 business days where the service was supplied as a result of direct marketing.</li>
          <li>Seek redress for defective or non-conforming services.</li>
        </ul>
        <p className="mt-4">
          Where CPA rights are applicable and more favourable than those set out in this Policy, the CPA
          provisions shall prevail.
        </p>
      </section>

      <Separator />

      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-text mb-4 tracking-tight">6. How to Request a Refund</h2>
        <p>To submit a refund request, please follow these steps:</p>
        <ul className="list-disc list-inside space-y-2 mt-4">
          <li><strong className="text-text">Step 1:</strong> Contact us at{' '}
            <a href="mailto:godfrey.kutumela@nucleus-systems.com?subject=Refund%20Request" className="text-brand hover:underline">
              godfrey.kutumela@nucleus-systems.com
            </a>{' '}
            with the subject line: "Refund Request — [Your Organisation Name]".</li>
          <li><strong className="text-text">Step 2:</strong> Include in your email: your registered email address, organisation name, subscription plan, date of payment, reason for the refund request, and whether any scans were performed.</li>
          <li><strong className="text-text">Step 3:</strong> Our billing team will review your request and respond within 5 (five) business days.</li>
          <li><strong className="text-text">Step 4:</strong> If approved, the refund will be processed to the original payment method within 7–10 business days, subject to your payment provider's timelines.</li>
        </ul>
        <Card className="border-border/40 bg-surface2 mt-4">
          <CardContent className="p-4">
            <p className="text-text2">
              <strong>Note:</strong> Refund requests submitted by any method other than email may not be
              processed. Please ensure your request is sent to the correct address.
            </p>
          </CardContent>
        </Card>
      </section>

      <Separator />

      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-text mb-4 tracking-tight">7. Cancellation of Subscription</h2>
        <p>
          You may cancel your Paxley subscription at any time through the platform settings or by
          contacting us at{' '}
          <a href="mailto:godfrey.kutumela@nucleus-systems.com" className="text-brand hover:underline">
            godfrey.kutumela@nucleus-systems.com
          </a>
          . Cancellation will take effect at the end of your current billing period. You will continue
          to have access to the Service until the end of the paid period.
        </p>
        <p className="mt-4">
          Cancellation does not automatically entitle you to a refund. Please refer to Section 3 for
          refund eligibility criteria.
        </p>
      </section>

      <Separator />

      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-text mb-4 tracking-tight">8. Disputed Charges</h2>
        <p>
          If you believe you have been incorrectly charged or notice an unauthorised charge on your
          payment statement, please contact us immediately at{' '}
          <a href="mailto:godfrey.kutumela@nucleus-systems.com" className="text-brand hover:underline">
            godfrey.kutumela@nucleus-systems.com
          </a>
          . We will investigate and respond within 5 business days.
        </p>
        <p className="mt-4">
          Please do not initiate a chargeback with your bank before contacting us first, as chargebacks
          may result in account suspension while the dispute is under investigation.
        </p>
      </section>

      <Separator />

      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-text mb-4 tracking-tight">9. Changes to This Policy</h2>
        <p>
          Nucleus Systems reserves the right to modify this Refund and Returns Policy at any time. Any
          changes will be effective from the date of publication on our website. For active subscribers,
          changes to this Policy will be communicated via email at least 14 days before taking effect.
        </p>
      </section>

      <Separator />

      <section>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-text mb-4 tracking-tight">10. Contact Us</h2>
        <p>For all refund, billing, or payment-related queries:</p>
        <Card className="border-border/30 bg-surface2 mt-4">
          <CardContent className="p-6 space-y-2">
            <p>
              <strong className="text-text">Billing Contact:</strong> Nucleus Systems (Pty) Ltd
            </p>
            <p className="text-sm">43 Silver Creek Eco Park Estates</p>
            <p className="text-sm">
              <strong className="text-text">Tel:</strong> +27 83 680 3427
            </p>
            <p className="text-sm">
              <strong className="text-text">Email:</strong>{' '}
              <a href="mailto:godfrey.kutumela@nucleus-systems.com" className="text-brand hover:underline">
                godfrey.kutumela@nucleus-systems.com
              </a>
            </p>
            <p className="text-sm">
              <strong className="text-text">Web:</strong>{' '}
              <a href="https://www.nucleus-systems.com" target="_blank" rel="noopener noreferrer" className="text-brand hover:underline">
                www.nucleus-systems.com
              </a>
            </p>
            <p className="text-sm">
              <strong className="text-text">Web Developer:</strong> Godfrey Kutumela
            </p>
          </CardContent>
        </Card>
      </section>
    </LegalPageLayout>
  );
};

export default Refund;
